import { useSandboxContext } from '@appfabric/providers';

interface AppFabricAnalytics {
  // AppFabric track accepts a TrackingEvent object (Record<string, any>)
  // This matches the SandboxAnalytics.track signature from @appfabric/sandbox-spec
  // Usage: track({ event: 'event_name', properties: {...} })
  track?: (trackingEvent: Record<string, unknown>) => void;
}

export interface AppFabricInstance {
  analytics?: AppFabricAnalytics;
  performance?: {
    createProfiler?: (pathname: string) => {
      start: () => void;
      stop: () => void;
      _mainSpan: { startTime: number };
    };
  };
  navigation?: object;
  pubsub?: {
    subscribe?: (topic: string, callback: (data?: unknown) => void) => void;
    publish?: (topic: string, data?: unknown) => void;
  };
  appContext?: {
    getEnvironment?: () => string;
  };
}

export interface SegmentAnalytics {
  track?: (event: string, properties?: object) => void;
  initialized?: boolean;
  flush?: () => void;
}

declare global {
  interface Window {
    appFabricEnvironment?: string | null;
    appFabricInstance?: AppFabricInstance | null;
    __mcSandboxCache?: { analytics?: AppFabricAnalytics } | null;
    analytics?: SegmentAnalytics | null;
  }
}

/**
 * Get the cached sandbox from the global window object.
 * The sandbox is cached by the Router component when on Intuit domain.
 */
export const getCachedSandbox = (): Window['__mcSandboxCache'] => {
  return window.__mcSandboxCache ?? null;
};

/**
 * Retrieves the current application environment based on various contexts.
 * Environments: mailchimp or appfabric
 *
 * This function first checks if the application is running in a sandbox context
 * and returns the corresponding environment. If not, it checks the configuration
 * for an interop environment and returns it. If neither is available, it defaults
 * to returning 'mailchimp'.
 *
 * @returns {string} The current environment as a string, or false if not found.
 */
export const useAppFabricEnvironment = (): string => {
  const sandbox = useSandboxContext();
  if (sandbox) {
    return 'appfabric';
  }
  // Fallback environment
  return 'mailchimp';
};

/**
 * Get the current environment including enviroment type.
 * Environments: mailchimp, appfabric_e2e, appfabric_prod
 *
 * This function checks if the application is running in a sandbox context. If not, it checks
 * the configuration for an interop environment and returns it. If neither
 * is available, it defaults to returning 'mailchimp'.
 *
 * @returns {string | boolean} The current environment as a string, or false if not found.
 */
export const useFullEnvironment = (): string | boolean => {
  const sandbox = useSandboxContext();
  // Check Appfabric context
  if (sandbox) {
    const appEnvironment = sandbox?.appContext?.getEnvironment() ?? false;
    return 'appfabric_' + appEnvironment;
  }
  // Fallback environment
  return 'mailchimp';
};

/**
 * Get environment types: e2e, prod, preprod
 *
 * @returns {string | boolean} The current environment as a string or false if not available.
 */
export const useEnvironmentType = (): string | boolean => {
  const sandbox = useSandboxContext();
  // Check Appfabric context
  if (sandbox) {
    const appEnvironment = sandbox?.appContext?.getEnvironment() ?? false;
    return appEnvironment;
  }
  return false;
};

/**
 * Retrieves the current application environment from the window object.
 *
 * @returns {string | null} The current environment as a string or null if not available.
 */
export const getAppFabricEnv = (): string | null =>
  window.appFabricEnvironment ?? null;

/**
 * Get the AppFabric instance from the global window object.
 * This is a non-hook utility that can be used outside of React components.
 *
 * @returns {object | null} The AppFabric instance or null if not available.
 */
export const getAppFabricInstance = (): Window['appFabricInstance'] => {
  return window.appFabricInstance ?? null;
};

/**
 * Get the global Segment analytics instance.
 * In AppFabric environments, this is available as window.analytics.
 * This is a non-hook utility that can be used outside of React components.
 *
 * @returns {object | null} The analytics instance or null if not available.
 */
export const getGlobalAnalytics = (): Window['analytics'] => {
  return window.analytics ?? null;
};

/**
 * Returns the sandbox context only when the environment is AppFabric.
 * This is useful for components that need the sandbox context but only
 * when running in an AppFabric environment.
 *
 * @returns {object | null} The sandbox context object if in AppFabric environment, null otherwise.
 */
export const useAppFabricSandbox = (): object | null => {
  const environment = useAppFabricEnvironment();
  const sandbox = useSandboxContext();

  if (environment === 'appfabric') {
    return sandbox;
  }
  return null;
};
